import type { ModalContentItem } from './types';

/**
 * // Class Obj: モーダルに表示する汎用コンテンツ群を管理
 * // Note:
 * // - data-modal-content-item を起点に、表示対象の content を収集します。
 * // - group / index を保持することで、next-item や next-group の移動計算に使える形にします。
 * // - 画像・動画に限定せず、口コミ、商品詳細、任意HTMLなどを扱えます。
 */
export class ModalContentCollection
{
  private static readonly DEFAULT_GROUP_NAME = 'default';

  private static readonly SELECTOR_CONTENT_ITEM = '[data-modal-content-item]';
  private static readonly SELECTOR_CONTENT_SOURCE = '[data-modal-content-source]';
  private static readonly SELECTOR_CHOOSE_SOURCE = '[data-modal-choose-source]';
  private static readonly SELECTOR_MODAL = '[data-modal]';

  private static readonly ATTR_CONTENT_GROUP = 'data-modal-content-group';
  private static readonly ATTR_CONTENT_INDEX = 'data-modal-content-index';
  private static readonly ATTR_CONTENT_TARGET = 'data-modal-content-target';

  private readonly mRoot: HTMLElement;
  private readonly mContentItemMap: WeakMap<HTMLElement, ModalContentItem> = new WeakMap();
  private readonly mGroupItemsMap: Map<string, ModalContentItem[]> = new Map();

  /**
   * // Method: 汎用コンテンツの収集対象 root を設定し、初期収集を実行する
   */
  constructor(root: HTMLElement | null)
  {
    this.mRoot = root ?? document.body;

    this.setupItems();
  }

  /**
   * // Method: content item と content の対応関係を作成する
   * // Note:
   * // - data-modal-content-target があれば、指定IDの要素を content として扱います。
   * // - 指定がなければ content item 内の data-modal-content-source を content として扱います。
   */
  private setupItems(): void
  {
    const content_item_elements: HTMLElement[] = Array.from(
      this.mRoot.querySelectorAll<HTMLElement>(ModalContentCollection.SELECTOR_CONTENT_ITEM),
    );

    const group_count_map: Map<string, number> = new Map();

    content_item_elements.forEach((content_item_element: HTMLElement) =>
    {
      const content: HTMLElement | null = this.getContentSourceByItem(content_item_element);

      if (!content)
      {
        return;
      }

      const group_name: string = this.getGroupNameByItem(content_item_element);
      const item_index: number = this.getItemIndexByItem(content_item_element, group_name, group_count_map);
      const choose_content: HTMLElement | null = this.getChooseSourceByItem(content_item_element);

      this.prepareModalSourceElement(content_item_element, content);

      if (choose_content)
      {
        this.prepareModalSourceElement(content_item_element, choose_content);
      }

      const content_item: ModalContentItem = {
        group_name,
        item_index,
        element: content_item_element,
        content,
        choose_content,
      };

      this.mContentItemMap.set(content_item_element, content_item);

      const group_items: ModalContentItem[] = this.mGroupItemsMap.get(group_name) ?? [];
      group_items.push(content_item);

      this.mGroupItemsMap.set(group_name, group_items);
    });

    this.sortItems();
  }

  /**
   * // Method: グループ内の item を index 順に並び替える
   */
  private sortItems(): void
  {
    this.mGroupItemsMap.forEach((items: ModalContentItem[]) =>
    {
      items.sort((first_item: ModalContentItem, second_item: ModalContentItem) =>
      {
        return first_item.item_index - second_item.item_index;
      });
    });
  }

  /**
   * // Method: content item から group 名を取得する
   */
  private getGroupNameByItem(content_item_element: HTMLElement): string
  {
    return content_item_element.getAttribute(ModalContentCollection.ATTR_CONTENT_GROUP)
      ?? ModalContentCollection.DEFAULT_GROUP_NAME;
  }

  /**
   * // Method: content item から item index を取得する
   * // Note:
   * // - data-modal-content-index があればその値を優先します。
   * // - 指定がない場合は、同一 group 内の登場順を index として扱います。
   */
  private getItemIndexByItem(
    content_item_element: HTMLElement,
    group_name: string,
    group_count_map: Map<string, number>,
  ): number
  {
    const index_value: string | null = content_item_element.getAttribute(ModalContentCollection.ATTR_CONTENT_INDEX);

    if (index_value !== null)
    {
      const parsed_index: number = Number(index_value);

      if (!Number.isNaN(parsed_index))
      {
        return parsed_index;
      }
    }

    const current_count: number = group_count_map.get(group_name) ?? 0;
    group_count_map.set(group_name, current_count + 1);

    return current_count;
  }

  /**
   * // Method: content item に対応する表示元 content を取得する
   */
  private getContentSourceByItem(content_item_element: HTMLElement): HTMLElement | null
  {
    const target_id: string | null = content_item_element.getAttribute(ModalContentCollection.ATTR_CONTENT_TARGET);

    if (target_id)
    {
      return document.getElementById(target_id.replace(/^#/, ''));
    }

    return content_item_element.querySelector<HTMLElement>(
      `:scope > ${ModalContentCollection.SELECTOR_CONTENT_SOURCE}`,
    ) ?? content_item_element.querySelector<HTMLElement>(
      ModalContentCollection.SELECTOR_CONTENT_SOURCE,
    );
  }

  /**
   * // Method: content item に対応する選択リスト用 content を取得する
   * // Note:
   * // - data-modal-choose-source があれば、選択リスト上の表示内容として優先します。
   * // - 指定がない場合は null を返し、呼び出し側で content source を fallback として扱います。
   */
  private getChooseSourceByItem(content_item_element: HTMLElement): HTMLElement | null
  {
    return content_item_element.querySelector<HTMLElement>(
      `:scope > ${ModalContentCollection.SELECTOR_CHOOSE_SOURCE}`,
    ) ?? content_item_element.querySelector<HTMLElement>(
      ModalContentCollection.SELECTOR_CHOOSE_SOURCE,
    );
  }

  /**
   * // Method: モーダル内に置いた表示元要素を初期表示から隠す
   * // Note:
   * // - data source を modal 内に直接置いた場合、clone 描画後に元要素まで表示される重複を防ぎます。
   * // - modal 外の open item や外部 target はページ上の見た目として使う可能性があるため、ここでは隠しません。
   */
  private prepareModalSourceElement(
    content_item_element: HTMLElement,
    source_element: HTMLElement,
  ): void
  {
    if (!source_element.closest(ModalContentCollection.SELECTOR_MODAL))
    {
      return;
    }

    source_element.hidden = true;
    source_element.setAttribute('aria-hidden', 'true');
  }

  /**
   * // Method: content item 要素に対応する ModalContentItem を取得する
   */
  public getItemByContentItem(content_item_element: HTMLElement): ModalContentItem | null
  {
    return this.mContentItemMap.get(content_item_element) ?? null;
  }

  /**
   * // Method: 任意の要素から最も近い content item を探し、対応する ModalContentItem を取得する
   * // Note:
   * // - button[data-modal-open] から呼ばれても、親方向の data-modal-content-item を参照できます。
   * // - data-modal-content-item 自身から呼ばれた場合も、そのまま取得できます。
   */
  public getItemByElement(element: HTMLElement): ModalContentItem | null
  {
    const content_item_element: HTMLElement | null = element.closest<HTMLElement>(
      ModalContentCollection.SELECTOR_CONTENT_ITEM,
    );

    if (!content_item_element)
    {
      return null;
    }

    return this.getItemByContentItem(content_item_element);
  }

  /**
   * // Method: 指定 group に属する item 一覧を取得する
   */
  public getItemsByGroup(group_name: string): ModalContentItem[]
  {
    return this.mGroupItemsMap.get(group_name) ?? [];
  }

  /**
   * // Method: 登録済みの group 名一覧を取得する
   */
  public getGroupNames(): string[]
  {
    return Array.from(this.mGroupItemsMap.keys());
  }

  /**
   * // Method: 指定 item が group 内で何番目にあるか取得する
   * // Note:
   * // - 返り値は 0 始まりの配列位置です。
   * // - 表示用の「1 / 5」などへ使う場合は、呼び出し側で +1 します。
   */
  public getItemPositionInGroup(content_item: ModalContentItem): number | null
  {
    const items: ModalContentItem[] = this.getItemsByGroup(content_item.group_name);

    const item_position: number = items.findIndex((item: ModalContentItem) =>
    {
      return item.element === content_item.element;
    });

    return item_position >= 0 ? item_position : null;
  }

  /**
   * // Method: 指定 group に属する item の総数を取得する
   */
  public getItemCountByGroup(group_name: string): number
  {
    return this.getItemsByGroup(group_name).length;
  }

  /**
   * // Method: 指定 group が全 group の中で何番目にあるか取得する
   * // Note:
   * // - 返り値は 0 始まりの配列位置です。
   * // - 登録順を基準に、現在 group の位置を解決します。
   */
  public getGroupPosition(group_name: string): number | null
  {
    const group_position: number = this.getGroupNames().findIndex((current_group_name: string) =>
    {
      return current_group_name === group_name;
    });

    return group_position >= 0 ? group_position : null;
  }

  /**
   * // Method: 登録済み group の総数を取得する
   */
  public getGroupCount(): number
  {
    return this.getGroupNames().length;
  }

  /**
   * // Method: group 名と index から item を取得する
   */
  public getItemByPosition(group_name: string, item_index: number): ModalContentItem | null
  {
    const items: ModalContentItem[] = this.getItemsByGroup(group_name);

    return items.find((item: ModalContentItem) =>
    {
      return item.item_index === item_index;
    }) ?? null;
  }
}
